import { useEffect, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { OptionType } from '~/features/types';
import { loadSubspecialtyOptions } from '../subspecialties';

/** The subspecialty options for the chosen specialty; empty until one is chosen. */
export const useSubspecialtyOptions = (specialtyId: string | undefined): OptionType[] => {
  const [loaded, setLoaded] = useState<{ specialtyId: string; options: OptionType[] }>();

  useEffect(() => {
    if (!specialtyId) return;
    let cancelled = false;
    loadSubspecialtyOptions(specialtyId)
      .then((options) => !cancelled && setLoaded({ specialtyId, options }))
      .catch(() => !cancelled && setLoaded({ specialtyId, options: [] }));
    return () => {
      cancelled = true;
    };
  }, [specialtyId]);

  // Ignore options that were loaded for a specialty that is no longer chosen.
  return loaded && loaded.specialtyId === specialtyId ? loaded.options : [];
};

/**
 * Subspecialties belong to one specialty, so when the USER picks a different
 * specialty (or a different provider category, which swaps the specialty list)
 * they are dropped. Only user changes count: react-hook-form tags a field's
 * own `onChange` with `type: 'change'`, whereas `reset` and `setValue` carry no
 * type, so loading a saved listing into the form never clears anything.
 *
 * The form (passed in, since a form component calls this above its own
 * provider) must have `providerCategory`, `aoiSpecialty` and `subspecialties`;
 * `resetGeneralFlag` also puts `generalSpecialtyFlag` back to its default.
 *
 * `clearSpecialtyWithCategory` also sets the specialty to the form's own "no
 * specialty" value (`empty`) when the category changes: the listing wizards
 * need it because a specialty left over from another category would be refused
 * by the API, and the B2B search filter because its specialty list is scoped to
 * the category.
 */
export const useResetSubspecialties = <TFieldValues extends FieldValues>(
  form: Pick<UseFormReturn<TFieldValues>, 'watch' | 'setValue'>,
  {
    resetGeneralFlag = false,
    clearSpecialtyWithCategory,
  }: { resetGeneralFlag?: boolean; clearSpecialtyWithCategory?: { empty: unknown } } = {}
) => {
  // The field names are fixed by the contract above, which the form's own value type cannot say.
  const { watch, setValue } = form as unknown as Pick<
    UseFormReturn<FieldValues>,
    'watch' | 'setValue'
  >;

  useEffect(() => {
    const subscription = watch((_values, { name, type }) => {
      if (type !== 'change') return;
      if (name !== 'providerCategory' && name !== 'aoiSpecialty') return;
      if (name === 'providerCategory' && clearSpecialtyWithCategory) {
        setValue('aoiSpecialty', clearSpecialtyWithCategory.empty);
      }
      setValue('subspecialties', []);
      if (resetGeneralFlag) setValue('generalSpecialtyFlag', true);
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue, clearSpecialtyWithCategory, resetGeneralFlag]);
};
